import type { StoreApi } from 'zustand';
import { superAdminApi } from '../api/superAdminApi';
import { getAdminApiError } from '../lib/adminApiErrors';
import type {
  AdminApiError,
  AdminBulkResponse,
  AdminCompanyDto,
} from '../types';
import type { SuperAdminActions, SuperAdminState } from './useSuperAdminStore';

type AdminStore = SuperAdminState & SuperAdminActions;
type SetAdminStore = StoreApi<AdminStore>['setState'];
type GetAdminStore = StoreApi<AdminStore>['getState'];
type CompanyCollectionVersion = { current: number };
type CompanyRequestActivity = ReturnType<typeof createCompanyRequestActivity>;
type CompanyActionKeys =
  | 'fetchCompanies'
  | 'loadCompanyDetail'
  | 'clearCompanyDetail'
  | 'createCompany'
  | 'saveCompany'
  | 'deleteCompany'
  | 'bulkCompanies';

const upsertCompany = (
  companies: readonly AdminCompanyDto[],
  canonical: AdminCompanyDto
): AdminCompanyDto[] => {
  const next = companies.some((company) => company.id === canonical.id)
    ? companies.map((company) => company.id === canonical.id ? canonical : company)
    : [...companies, canonical];
  return next.sort((left, right) => left.name.localeCompare(
    right.name,
    undefined,
    { sensitivity: 'base' }
  ) || left.id - right.id);
};

const createCompanyRequestActivity = (set: SetAdminStore) => {
  let pendingRequests = 0;
  return {
    begin: () => {
      pendingRequests += 1;
      set({ companyLoading: true, companyError: null });
    },
    finish: () => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      set({ companyLoading: pendingRequests > 0 });
    },
  };
};

const fetchCompaniesAction = (
  set: SetAdminStore,
  activity: CompanyRequestActivity,
  version: CompanyCollectionVersion
) => async () => {
  const requestVersion = version.current;
  activity.begin();
  try {
    const companies = await superAdminApi.getCompanies();
    if (requestVersion === version.current) {
      set({ companies });
    }
  } catch (error) {
    if (requestVersion === version.current) {
      const failure = getAdminApiError(error, 'company_list_failed');
      set({ companyError: failure.message, error: failure.message });
    }
  } finally {
    activity.finish();
  }
};

const createCompanyDetailActions = (set: SetAdminStore) => {
  let requestGeneration = 0;
  const loadCompanyDetail = async (id: number) => {
    const generation = ++requestGeneration;
    set({ companyDetail: null, companyLoading: true, companyError: null });
    try {
      const companyDetail = await superAdminApi.getCompany(id);
      if (generation === requestGeneration) {
        set({ companyDetail, companyLoading: false });
      }
      return companyDetail;
    } catch (error) {
      if (generation === requestGeneration) {
        set({ companyError: getAdminApiError(error, 'company_detail_failed').message, companyLoading: false });
      }
      throw error;
    }
  };
  const clearCompanyDetail = () => {
    requestGeneration += 1;
    set({ companyDetail: null, companyLoading: false, companyError: null });
  };
  return { loadCompanyDetail, clearCompanyDetail };
};

const refreshCompanies = async (): Promise<
  { companies: AdminCompanyDto[]; error: null }
  | { companies: null; error: AdminApiError }
> => {
  try {
    return { companies: await superAdminApi.getCompanies(), error: null };
  } catch (error) {
    return { companies: null, error: getAdminApiError(error, 'company_refresh_failed') };
  }
};

const createCompanyAction: (
  set: SetAdminStore,
  activity: CompanyRequestActivity,
  version: CompanyCollectionVersion
) => SuperAdminActions['createCompany'] = (set, activity, version) => async (request) => {
  activity.begin();
  try {
    const created = await superAdminApi.createCompany(request);
    version.current += 1;
    set((state) => ({
      companies: upsertCompany(state.companies, created),
      companyDetail: created,
    }));
    return created;
  } catch (error) {
    set({ companyError: getAdminApiError(error, 'company_create_failed').message });
    throw error;
  } finally {
    activity.finish();
  }
};

const saveCompanyAction: (
  set: SetAdminStore,
  activity: CompanyRequestActivity,
  version: CompanyCollectionVersion
) => SuperAdminActions['saveCompany'] = (set, activity, version) => async (id, patch) => {
  activity.begin();
  try {
    const canonical = await superAdminApi.updateCompany(id, patch);
    version.current += 1;
    set((state) => ({
      companies: upsertCompany(state.companies, canonical),
      companyDetail: state.companyDetail?.id === id ? canonical : state.companyDetail,
    }));
    return canonical;
  } catch (error) {
    set({ companyError: getAdminApiError(error, 'company_update_failed').message });
    throw error;
  } finally {
    activity.finish();
  }
};

const applyBulkSuccesses = (
  companies: readonly AdminCompanyDto[],
  action: Parameters<SuperAdminActions['bulkCompanies']>[0],
  succeededIds: readonly number[]
): AdminCompanyDto[] => {
  const succeeded = new Set(succeededIds);
  if (action === 'delete') {
    return companies.filter((company) => !succeeded.has(company.id));
  }
  return companies.map((company) => succeeded.has(company.id)
    ? { ...company, status: 'suspended' as const }
    : company);
};

const deleteCompanyAction: (
  set: SetAdminStore,
  get: GetAdminStore,
  activity: CompanyRequestActivity,
  version: CompanyCollectionVersion
) => SuperAdminActions['deleteCompany'] = (set, get, activity, version) => async (id, options) => {
  activity.begin();
  try {
    await superAdminApi.deleteCompany(id, options);
    const mutationVersion = ++version.current;
    set((state) => ({
      companies: state.companies.filter((company) => company.id !== id),
      companyDetail: state.companyDetail?.id === id ? null : state.companyDetail,
    }));
    const refresh = await refreshCompanies();
    if (refresh.companies && mutationVersion === version.current) {
      set({ companies: refresh.companies });
      return { succeeded: true, refreshError: null };
    }
    if (!refresh.companies && mutationVersion === version.current) {
      set({ companyError: refresh.error.message });
    }
    return { succeeded: true, refreshError: refresh.error };
  } catch (error) {
    if (get().companyDetail?.id === id) {
      try {
        set({ companyDetail: await superAdminApi.getCompany(id) });
      } catch {
        set({ companyError: getAdminApiError(error, 'company_delete_failed').message });
      }
    } else {
      set({ companyError: getAdminApiError(error, 'company_delete_failed').message });
    }
    throw error;
  } finally {
    activity.finish();
  }
};

const bulkCompaniesAction: (
  set: SetAdminStore,
  activity: CompanyRequestActivity,
  version: CompanyCollectionVersion
) => SuperAdminActions['bulkCompanies'] = (set, activity, version) => async (action, ids, expectedCounts) => {
  activity.begin();
  try {
    const result: AdminBulkResponse = await superAdminApi.bulkCompanies(action, ids, expectedCounts);
    const mutationVersion = ++version.current;
    set((state) => ({ companies: applyBulkSuccesses(state.companies, action, result.succeeded) }));
    const refresh = await refreshCompanies();
    if (refresh.companies && mutationVersion === version.current) {
      set({ companies: refresh.companies });
      return { ...result, refreshError: null };
    }
    if (!refresh.companies && mutationVersion === version.current) {
      set({ companyError: refresh.error.message });
    }
    return { ...result, refreshError: refresh.error };
  } catch (error) {
    set({ companyError: getAdminApiError(error, 'company_bulk_failed').message });
    throw error;
  } finally {
    activity.finish();
  }
};

export const createCompanyStoreActions = (
  set: SetAdminStore,
  get: GetAdminStore
): Pick<SuperAdminActions, CompanyActionKeys> => {
  const activity = createCompanyRequestActivity(set);
  const version = { current: 0 };
  const detailActions = createCompanyDetailActions(set);
  return {
    fetchCompanies: fetchCompaniesAction(set, activity, version),
    ...detailActions,
    createCompany: createCompanyAction(set, activity, version),
    saveCompany: saveCompanyAction(set, activity, version),
    deleteCompany: deleteCompanyAction(set, get, activity, version),
    bulkCompanies: bulkCompaniesAction(set, activity, version),
  };
};
